import type { FC } from 'react';
// eslint-disable-next-line import/no-duplicates
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { defineMessages, useIntl } from 'react-intl';
import { useFragment } from 'react-relay';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Flex from '@atlaskit/primitives/components/flex';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { useContentState } from '@confluence/content-state/entry-points/useContentState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ReactionsSummaryViewPlaceholder } from '../entry-points/ReactionsSummaryViewPlaceholder.tsx';
import { ReactionsSSRLoader } from '../entry-points/ReactionsSSRLoader.tsx';

import { ReactionsLoaderBackground as DelayedReactions } from './ReactionsLoader.tsx';
import type { ReactionLocation, ReactionsData } from './reactionTypes.ts';
import { ReactionContainerType } from './reactionTypes.ts';
import { useReactionsQueryParams } from './hooks/useReactionsQueryParams.ts';
import { useIsReactionsDisabledForSite } from '@confluence/comment-context/entry-points/useIsReactionsDisabledForSite.ts';
import { fg } from '@confluence/feature-gating';
import { ReactionsContentFragment_content } from './graphql/ReactionsContentFragment.graphql.ts';
import type { ReactionsContentFragment_content$key } from './graphql/__generated__/ReactionsContentFragment_content.graphql.ts';

const i18n = defineMessages({
	addNew: {
		id: 'reactions.view-page.byline.reactions.add.new',
		description: 'Message displayed in button to add a new reaction.',
		defaultMessage: 'Add new',
	},
	addReaction: {
		id: 'reactions.view-page.byline.reactions.add.reaction',
		description:
			'The text is shown as a tooltip or button label in the byline reactions area of a Confluence view page. When the user interacts with this element, it allows them to add an emoji reaction to the page.',
		defaultMessage: 'Add reaction',
	},
});

const ViewPageReactionsContainerCommonStyles = xcss({
	display: 'flex',
	clear: 'both',
	overflow: 'visible',
	maxWidth: 'unset',
	margin: 'unset',
	marginBottom: 'space.025',
	marginTop: 'space.025',
	height: '28px',
});

type ViewPageReactionsProps = {
	contentId: string;
	contentType?: string | null;
	spaceId?: string | null;
	contentSubType?: string | null;
	noFlexWrap?: boolean;
	noRelativeContainer?: boolean;
	reactionsContentFragmentRef?: ReactionsContentFragment_content$key | null;
};

type ReactionsComponentProps = ViewPageReactionsProps & {
	reactionsData?: ReactionsData;
};

const ReactionsComponent: FC<ReactionsComponentProps> = ({
	contentId,
	contentType,
	spaceId,
	contentSubType,
	noFlexWrap,
	noRelativeContainer,
	reactionsData,
}) => {
	const { formatMessage } = useIntl();
	const [state] = useContentState();
	const { focusedPageReactionsId } = useReactionsQueryParams();
	const { edition } = useSessionData();
	const [reactionsRef, reactionsSectionInView] = useInView({
		rootMargin: '-100px 0px 0px 0px',
		triggerOnce: true,
	});

	// Used when we want to allow the user to select reactions from the summary view popup
	const customIcon = (
		<EmojiAddIcon testId="emoji-add-icon" color={token('color.icon.subtle')} label="" />
	);

	// Check whether we're on the server, or if in SPA but the page was initially SSR'd
	const isSSR =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	// In the browser, defer loading reactions until the section is visible and content is ready.
	const isInViewAndNotOnServer =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR && reactionsSectionInView && state?.isContentReady;

	const displayReactions = () => {
		if (contentType && spaceId) {
			// We want to load in the background ONLY if we have a query param, otherwise we can preload the query
			const ReactionsLoader = focusedPageReactionsId ? DelayedReactions : ReactionsSSRLoader;

			return (
				<Flex
					testId="view-page-byline-reactions-wrapper"
					data-vc="view-page-byline-reactions-wrapper"
				>
					<ReactionsLoader
						contentId={contentId}
						location={contentType as ReactionLocation}
						containerId={spaceId}
						containerType={ReactionContainerType.SPACE}
						contentSubType={contentSubType}
						reactionsData={reactionsData}
						summaryViewEnabled
						summaryViewThreshold={1}
						allowSelectFromSummaryView
						useButtonAlignmentStyling
						summaryViewPlacement="bottom-start"
						customAnalyticsAttributes={{
							edition,
						}}
						isSubtle
						reactionPickerTriggerIcon={customIcon}
						useDefaultStyledContainer
						noFlexWrap={noFlexWrap}
						noRelativeContainer={noRelativeContainer}
						reactionPickerTriggerText={formatMessage(i18n.addNew)}
						hoverableSummaryView
						optimisticallyLoadSummaryEmojis={isSSR}
						renderParticleEffectOnSummaryView
					/>
				</Flex>
			);
		} else {
			return <ReactionsSummaryViewPlaceholder />;
		}
	};

	return (
		<Box
			ref={reactionsRef}
			testId="view-page-byline-reactions"
			xcss={[ViewPageReactionsContainerCommonStyles]}
		>
			{isSSR || isInViewAndNotOnServer ? displayReactions() : <ReactionsSummaryViewPlaceholder />}
		</Box>
	);
};

export const ViewPageBylineReactions: FC<ViewPageReactionsProps> = (props) => {
	const isReactionsDisabledForSite = useIsReactionsDisabledForSite(props.contentId);
	if (isReactionsDisabledForSite && fg('confluence_hide_reactions_when_disabled')) {
		return null;
	}

	const reactions = props.reactionsContentFragmentRef ? (
		<ViewPageBylineReactionsFragmentReader
			{...props}
			reactionsContentFragmentRef={props.reactionsContentFragmentRef}
		/>
	) : (
		<ReactionsComponent {...props} />
	);

	return (
		<TransparentErrorBoundary attribution={Attribution.NOTIFICATIONS}>
			{reactions}
		</TransparentErrorBoundary>
	);
};

const ViewPageBylineReactionsFragmentReader: FC<
	ViewPageReactionsProps & {
		reactionsContentFragmentRef: ReactionsContentFragment_content$key;
	}
> = ({ reactionsContentFragmentRef, ...props }) => {
	const content = useFragment(ReactionsContentFragment_content, reactionsContentFragmentRef);

	return <ReactionsComponent {...props} reactionsData={content.contentReactionsSummary} />;
};
